import {
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  InputRightAddon,
  Text,
} from '@chakra-ui/react';
import { useActiveSession } from '@texas/hooks/useSession';
import { weightDecimalPlaces } from '@texas/resources/constants';
import { SimpleNote } from '@texas/components/shared/SimpleNote';
import {
  convertWeight,
  formatWeightInput,
  isValidStoredWeight,
  materialWeightRules,
  WeightRules,
  parseWeightForStorage,
  resolveWeightUnit,
  WeightUnit,
  weightUnits,
} from '@texas/resources/weightUnits';
import { forwardRef, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface WeightInputProps {
  value?: number | null;
  onChange: (kilograms: number | null) => void;
  onBlur?: () => void;
  onCommit?: () => void;
  onValidityChange?: (valid: boolean) => void;
  rules?: WeightRules;
  isDisabled?: boolean;
  error?: string;
  contextKey?: string | number;
  name?: string;
}

export const WeightInput = forwardRef<HTMLInputElement, WeightInputProps>(
  function WeightInput(
    {
      value,
      onChange,
      onBlur,
      onCommit,
      onValidityChange,
      rules = materialWeightRules,
      isDisabled = false,
      error,
      contextKey,
      name,
    },
    ref,
  ) {
    const { decimalPlaces, minimumPositiveWeight, emptyValue } = rules;
    const { t } = useTranslation();
    const { currentUser } = useActiveSession();
    const unit = resolveWeightUnit(currentUser.preferredWeightUnit);
    const id = useId();
    const [amount, setAmount] = useState(() => formatAmount(value, unit));
    const emittedValue = useRef(value);
    const committedValue = useRef(value);
    const previousContext = useRef({ unit, contextKey, isDisabled });
    const invalidAmount = !isValidStoredWeight(
      parseWeightForStorage(
        amount,
        unit,
        decimalPlaces,
        minimumPositiveWeight,
        emptyValue,
      ),
      decimalPlaces,
    );

    useEffect(() => {
      const previous = previousContext.current;
      if (
        previous.unit !== unit ||
        previous.contextKey !== contextKey ||
        previous.isDisabled !== isDisabled ||
        !Object.is(value, emittedValue.current)
      ) {
        setAmount(formatAmount(value, unit));
        emittedValue.current = value;
        committedValue.current = value;
      }
      previousContext.current = { unit, contextKey, isDisabled };
    }, [value, unit, contextKey, isDisabled]);

    useEffect(() => {
      onValidityChange?.(isDisabled || !invalidAmount);
      return () => onValidityChange?.(true);
    }, [invalidAmount, isDisabled, onValidityChange]);

    const changeAmount = (text: string) => {
      const kilograms = parseWeightForStorage(
        text,
        unit,
        decimalPlaces,
        minimumPositiveWeight,
        emptyValue,
      );
      setAmount(text);
      emittedValue.current = kilograms;
      onChange(kilograms);
      onValidityChange?.(isValidStoredWeight(kilograms, decimalPlaces));
    };

    const commit = () => {
      if (
        !isDisabled &&
        isValidStoredWeight(emittedValue.current, decimalPlaces)
      ) {
        setAmount(formatAmount(emittedValue.current, unit));
      }
      onBlur?.();
      if (
        isDisabled ||
        !isValidStoredWeight(emittedValue.current, decimalPlaces) ||
        Object.is(emittedValue.current, committedValue.current)
      )
        return;
      committedValue.current = emittedValue.current;
      onCommit?.();
    };

    return (
      <FormControl isDisabled={isDisabled} isInvalid={!!error || invalidAmount}>
        <Flex alignItems="center" justifyContent="space-between" gap={2}>
          <FormLabel htmlFor={id} mb={0}>
            {t('composition.weight')}
          </FormLabel>
          {unit !== WeightUnit.Kilograms && (
            <Text
              as="span"
              fontSize="sm"
              title={t('weightConverter.storedWeight')}
            >
              {value != null && Number.isFinite(value)
                ? formatWeightInput(value)
                : '—'}{' '}
              kg
            </Text>
          )}
        </Flex>
        <InputGroup>
          <Input
            ref={ref}
            id={id}
            name={name}
            value={amount}
            onChange={(event) => changeAmount(event.target.value)}
            onBlur={commit}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' || !onCommit) return;
              event.preventDefault();
              event.stopPropagation();
              commit();
            }}
            inputMode="decimal"
            variant="texas"
            placeholder="0"
          />
          <InputRightAddon h="auto" border="none">
            {weightUnits[unit].symbol}
          </InputRightAddon>
        </InputGroup>
        <FormErrorMessage>
          {error ?? t('weightConverter.invalidWeight')}
        </FormErrorMessage>
        {unit !== WeightUnit.Kilograms &&
          value != null &&
          Number.isFinite(value) && (
            <SimpleNote
              mt={2}
              p={2}
              borderRadius="md"
              alignItems="flex-start"
              bg="yellow.900"
              color="yellow.100"
              _light={{ bg: 'yellow.50', color: 'yellow.800' }}
            >
              {t('weightConverter.roundingNote', {
                decimals: Math.min(decimalPlaces, weightDecimalPlaces),
                unit: weightUnits[unit].symbol,
              })}
            </SimpleNote>
          )}
      </FormControl>
    );
  },
);

function formatAmount(value: number | null | undefined, unit: WeightUnit) {
  return value != null && Number.isFinite(value)
    ? formatWeightInput(convertWeight(value, WeightUnit.Kilograms, unit))
    : '';
}
